import React, { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Upload, FileText, Trash2, Plus, Loader2 } from "lucide-react";
import * as api from "../../lib/api";

/** Upload a PDF/DOCX for a student, parse it (AI) into goals, review/edit, then save. */
export function ImportGoalsDialog({ open, onOpenChange, studentId, students = [], periods = [], onImported }) {
  const [step, setStep] = useState("upload"); // upload | preview
  const [sid, setSid] = useState(studentId || "");
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ student_name: "", goals: [] });
  const [periodId, setPeriodId] = useState("");

  const reset = () => { setStep("upload"); setSid(studentId || ""); setFile(null); setPreview({ student_name: "", goals: [] }); setPeriodId(""); };

  const doParse = async () => {
    const student = studentId || sid;
    if (!student) { toast.error("Select a student"); return; }
    if (!file) { toast.error("Choose a PDF or .docx file"); return; }
    setParsing(true);
    try {
      const r = await api.parseGoalDocument(student, file);
      setPreview(r.data);
      setStep("preview");
      if (!r.data.goals?.length) toast.warning("No goals were detected — you can add them manually or try another file.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to parse document");
    } finally { setParsing(false); }
  };

  const doConfirm = async () => {
    setSaving(true);
    try {
      await api.confirmGoalImport({
        student_id: studentId || sid,
        reporting_period_id: periodId || null,
        goals: preview.goals,
      });
      toast.success(`Imported ${preview.goals.length} goal(s)`);
      reset();
      onOpenChange(false);
      onImported && onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save goals");
    } finally { setSaving(false); }
  };

  // Editable-preview mutators
  const setGoal = (gi, patch) => setPreview((p) => ({ ...p, goals: p.goals.map((g, i) => (i === gi ? { ...g, ...patch } : g)) }));
  const setObj = (gi, oi, patch) => setPreview((p) => ({ ...p, goals: p.goals.map((g, i) => i !== gi ? g : { ...g, objectives: g.objectives.map((o, j) => (j === oi ? { ...o, ...patch } : o)) }) }));
  const setNote = (gi, oi, ni, patch) => setPreview((p) => ({ ...p, goals: p.goals.map((g, i) => i !== gi ? g : { ...g, objectives: g.objectives.map((o, j) => j !== oi ? o : { ...o, notes: o.notes.map((n, k) => (k === ni ? { ...n, ...patch } : n)) }) }) }));
  const delGoal = (gi) => setPreview((p) => ({ ...p, goals: p.goals.filter((_, i) => i !== gi) }));
  const delObj = (gi, oi) => setPreview((p) => ({ ...p, goals: p.goals.map((g, i) => i !== gi ? g : { ...g, objectives: g.objectives.filter((_, j) => j !== oi) }) }));
  const delNote = (gi, oi, ni) => setPreview((p) => ({ ...p, goals: p.goals.map((g, i) => i !== gi ? g : { ...g, objectives: g.objectives.map((o, j) => j !== oi ? o : { ...o, notes: o.notes.filter((_, k) => k !== ni) }) }) }));

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="import-goals-dialog">
        <DialogHeader>
          <DialogTitle>Import Goals from a Document</DialogTitle>
          <DialogDescription>Upload a PDF or Word (.docx) IEP goal report. The text is parsed with AI into goals you can review and edit before saving.</DialogDescription>
        </DialogHeader>

        {step === "upload" ? (
          <div className="space-y-3">
            {!studentId && (
              <div>
                <Label>Student</Label>
                <Select value={sid} onValueChange={setSid}>
                  <SelectTrigger data-testid="import-student-select"><SelectValue placeholder="Select a student" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Document (PDF or .docx)</Label>
              <Input type="file" accept=".pdf,.docx" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
              {file && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{file.name}</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">Review the extracted goals for <span className="font-semibold">{preview.student_name}</span>. Edit anything, then Save.</p>
            <div>
              <Label>Reporting Period (optional — applied to all imported goals)</Label>
              <Select value={periodId || "none"} onValueChange={(v) => setPeriodId(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="import-period-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No reporting period</SelectItem>
                  {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {preview.goals.length === 0 && <p className="text-sm text-slate-400 italic">No goals extracted.</p>}
            {preview.goals.map((g, gi) => (
              <div key={gi} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`preview-goal-${gi}`}>
                <div className="flex items-center gap-2">
                  <Input value={g.goal_type} onChange={(e) => setGoal(gi, { goal_type: e.target.value })} className="w-40" placeholder="Goal type" />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 ml-auto" onClick={() => delGoal(gi)}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <Textarea value={g.goal_statement} onChange={(e) => setGoal(gi, { goal_statement: e.target.value })} placeholder="Goal statement" className="min-h-[44px]" />
                <Textarea value={g.goal_recommendation} onChange={(e) => setGoal(gi, { goal_recommendation: e.target.value })} placeholder="Goal recommendation" className="min-h-[36px]" />
                <div className="space-y-2 pl-2 border-l-2 border-slate-100">
                  {g.objectives.map((o, oi) => (
                    <div key={oi} className="space-y-1">
                      <div className="flex items-start gap-1">
                        <span className="text-xs font-semibold text-slate-500 pt-2">{oi + 1}:</span>
                        <Textarea value={o.description} onChange={(e) => setObj(gi, oi, { description: e.target.value })} placeholder="Objective description" className="min-h-[40px]" />
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500 mt-1" onClick={() => delObj(gi, oi)}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                      <div className="flex gap-2 pl-5">
                        <Input value={o.progress_status} onChange={(e) => setObj(gi, oi, { progress_status: e.target.value })} placeholder="Current progress" className="w-44 h-8 text-xs" />
                        <Input value={o.measurement} onChange={(e) => setObj(gi, oi, { measurement: e.target.value })} placeholder="Measurement" className="w-44 h-8 text-xs" />
                      </div>
                      <div className="pl-5 space-y-1">
                        {o.notes.map((n, ni) => (
                          <div key={ni} className="flex items-start gap-1">
                            <Input type="date" value={n.date} onChange={(e) => setNote(gi, oi, ni, { date: e.target.value })} className="w-32 h-7 text-xs" />
                            <Textarea value={n.text} onChange={(e) => setNote(gi, oi, ni, { text: e.target.value })} className="min-h-[32px] text-xs" />
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => delNote(gi, oi, ni)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          {step === "upload" ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={doParse} disabled={parsing} data-testid="parse-document-btn">
                {parsing ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Parsing…</> : <><Upload className="w-4 h-4 mr-1" />Parse Document</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setStep("upload")}>Back</Button>
              <Button onClick={doConfirm} disabled={saving || preview.goals.length === 0} data-testid="confirm-import-btn">
                {saving ? "Saving…" : `Save ${preview.goals.length} Goal(s)`}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
